import React, { useState, useRef, useEffect, useMemo } from "react"
import { Bell, Search, Menu, Download, FileText, X, Check, Loader2, LogOut, Users, Shield, User, Globe, Briefcase, Building2, ListTodo, HelpCircle, PhoneCall, Headset, CheckSquare, Mail, MessageCircle, Sparkles, BarChart3, Database, Wrench, AlertTriangle } from "lucide-react"
import { Input } from "../ui/input"
import { Button } from "../ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar"
import { Badge } from "../ui/badge"
import { Card } from "../ui/card"
import { useAuth } from "../../context/AuthContext"
import { useJob } from "../../context/JobContext"
import { useMaintenance } from "../../context/MaintenanceContext"
import { cn } from "../../utils/cn"
import { Link, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { ThemeSwitcher } from "../ThemeSwitcher"
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import api from '../../services/api';
import { toast } from 'react-toastify';

const getJobTitle = (job, isMonitoring, isOpenBankingSync, isOpenBankingStatement, obStatementMessage) => {
  if (isMonitoring) {
    if (job.info?.kind === 'passp_fatture_print') {
      return 'Documento Passpartout';
    }
    if (job.info?.kind === 'passp_client_statement') {
      return 'Estratto conto cliente Passpartout';
    }
    if (job.info?.kind === 'modulistica_test_pdf') {
      return 'Modulistica – Test PDF';
    }
    return 'Monitoraggio Pratiche – performance';
  }

  if (job.type === 'cloudtalk_sync') {
    return job.status === 'completed'
      ? 'Sincronizzazione CloudTalk'
      : 'Errore sincronizzazione CloudTalk';
  }

  if (job.type === 'passp_fatture_sync') {
    return job.status === 'completed'
      ? 'Sincronizzazione Fatture Passpartout'
      : 'Errore sincronizzazione Fatture Passpartout';
  }

  if (job.type === 'passp_clienti_sync') {
    return job.status === 'completed'
      ? 'Sincronizzazione Clienti Passpartout'
      : 'Errore sincronizzazione Clienti Passpartout';
  }

  if (job.type === 'passp_conti_sync') {
    if (job.status === 'completed') {
      const azienda = job.result?.azienda || '';
      const anno = job.result?.anno || '';
      if (azienda && anno) {
        return `Sincronizzazione Conti di Bilancio - Azienda ${azienda} / ${anno}`;
      }
      return 'Sincronizzazione Conti di Bilancio';
    }
    return 'Errore sincronizzazione Conti di Bilancio';
  }

  if (job.type === 'passp_prima_nota_sync') {
    return job.status === 'completed'
      ? 'Sincronizzazione Prima Nota Passpartout'
      : 'Errore sincronizzazione Prima Nota Passpartout';
  }

  if (job.type === 'funnelix_import_sync') {
    return job.status === 'completed'
      ? 'Import contatti Funnelix'
      : 'Errore import contatti Funnelix';
  }

  if (job.type === 'open_banking_tx_csv_import') {
    return job.status === 'completed'
      ? 'Import transazioni CSV'
      : 'Errore import transazioni CSV';
  }

  if (job.type === 'funnelix_verifica_clienti_csv') {
    return job.status === 'completed'
      ? 'Verifica clienti Funnelix (CSV)'
      : 'Errore verifica clienti Funnelix';
  }

  if (job.type === 'funnelix_push_clienti') {
    return job.status === 'completed'
      ? 'Aggiornamento clienti su Funnelix'
      : 'Errore aggiornamento clienti su Funnelix';
  }

  if (job.type === 'pf_clienti_assign') {
    return job.status === 'completed' ? 'Assegnazione clienti' : 'Errore assegnazione clienti';
  }

  if (isOpenBankingSync) {
    return job.status === 'completed'
      ? 'Sincronizzazione Open Banking'
      : 'Errore sincronizzazione Open Banking';
  }

  if (isOpenBankingStatement) {
    if (job.status === 'completed') {
      return obStatementMessage || 'Estratto conto banche periodo';
    }
    return 'Errore generazione estratto conto banche';
  }

  if (job.type === 'customer_csv') {
    return job.status === 'completed'
      ? 'Esportazione pronta'
      : 'Errore esportazione';
  }

  if (job.type === 'cloudtalk_recall_csv') {
    return job.status === 'completed'
      ? 'Esportazione CSV per Cloudtalk (Dialer)'
      : 'Errore esportazione CSV per Cloudtalk (Dialer)';
  }

  if (job.type === 'dossier_recall_csv') {
    return job.status === 'completed'
      ? 'Esportazione CSV Recall Preventivi'
      : 'Errore esportazione CSV Recall Preventivi';
  }

  if (job.type === 'job_notturno') {
    return job.status === 'completed'
      ? 'Job Notturno completato'
      : 'Errore Job Notturno';
  }

  if (job.type === 'cloudtalk_calls') {
    if (job.status === 'completed') {
      return job.result?.message || `Report CloudTalk (ID: ${job.id})`;
    }
    return 'Errore generazione report CloudTalk';
  }

  if (job.type === 'scadenzario_import_csv') {
    const fileName = String(job.result?.originalFileName || job.meta?.originalFileName || '').trim();
    return job.status === 'completed'
      ? (fileName ? `Importazione piani completata – ${fileName}` : 'Importazione piani completata')
      : (fileName ? `Errore importazione piani – ${fileName}` : 'Errore importazione piani');
  }

  if (job.type === 'recessi_import_csv') {
    return job.status === 'completed' ? 'Import CSV Recessi' : 'Errore import CSV Recessi';
  }

  if (job.type === 'invii_modulistica_bulk_send_email') {
    return job.status === 'completed'
      ? 'Invio email promemoria completato'
      : 'Errore invio email promemoria';
  }

  if (job.type === 'passp_fatture_bulk_send_email') {
    return job.status === 'completed'
      ? 'Invio email documenti Passpartout'
      : 'Errore invio email documenti Passpartout';
  }

  if (job.type === 'scadenzario_performance_referenti_pdf') {
    return job.status === 'completed'
      ? 'Report Performance Referenti pronto'
      : 'Errore report Performance Referenti';
  }

  if (job.type === 'kpi_lead_yearly_pdf') {
    if (job.status === 'completed') {
      return job.result?.message || 'Report KPI Lead Annuale pronto';
    }
    return 'Errore report KPI Lead Annuale';
  }

  if (job.type === 'hr_absence_report') {
    return job.status === 'completed'
      ? 'Report assenze pronto'
      : 'Errore report assenze';
  }

  if (job.type === 'hr_time_variance_report') {
    if (job.status === 'completed') {
      const msg = String(job.result?.message || '').trim();
      const base = msg.includes(' pronto') ? msg.split(' pronto')[0].trim() : '';
      return base || 'Report Anticipi/Posticipi';
    }
    return 'Errore report Anticipi/Posticipi';
  }

  if (job.type === 'blacklist_segnalazioni_report') {
    if (job.status === 'completed') {
      const msg = String(job.result?.message || '').trim();
      const base = msg.includes(' pronto') ? msg.split(' pronto')[0].trim() : '';
      return base || 'Report Segnalazioni Blacklist';
    }
    return 'Errore report Segnalazioni Blacklist';
  }

  if (job.type === 'pratiche_monitoraggio') {
    return job.status === 'completed'
      ? 'Report monitoraggio pratiche pronto'
      : 'Errore report monitoraggio pratiche';
  }

  if (job.type === 'firme_report') {
    return job.status === 'completed'
      ? 'Report firme pronto'
      : 'Errore report firme';
  }

  if (job.type === 'firme_csv_report') {
    return job.status === 'completed'
      ? 'Esportazione CSV firme pronta'
      : 'Errore esportazione CSV firme';
  }

  if (job.type === 'legali_chiusure_report') {
    return job.status === 'completed'
      ? 'Report legali chiusure pronto'
      : 'Errore report legali chiusure';
  }

  if (job.type === 'legali_chiusure_csv_report') {
    return job.status === 'completed'
      ? 'Esportazione CSV legali chiusure pronta'
      : 'Errore esportazione CSV legali chiusure';
  }

  if (job.type === 'pratiche_uo_report') {
    return job.status === 'completed'
      ? 'Report Pratiche U/O'
      : 'Errore report Pratiche U/O';
  }

  if (job.type === 'pratiche_carichi_lavoro_report') {
    return job.status === 'completed'
      ? 'Report Carichi per Legale'
      : 'Errore report Carichi per Legale';
  }

  if (job.type === 'passp_posizione_contabile_report') {
    const clientName = job?.result?.clientName || '';
    if (job.status === 'completed') {
      return `Report posizione contabile "${clientName || 'Cliente'}"`;
    }
    return 'Errore report posizione contabile';
  }

  if (job.type === 'admin_activity_summary') {
    if (job.status === 'completed') {
      const metaLabel = String(job.meta?.userLabel || '').trim();
      return metaLabel ? `Report attività (${metaLabel})` : 'Report attività pronto';
    }
    return 'Errore report attività';
  }

  const resultFilename = job.result?.fileName || job.result?.filename;
  if (typeof resultFilename === 'string' && /\.csv$/i.test(resultFilename)) {
    return job.status === 'completed' ? 'Esportazione CSV pronta' : 'Errore esportazione CSV';
  }

  return job.status === 'completed' ? 'Report pronto' : 'Errore generazione';
};

const getJobSubtitle = (job, isMonitoring, isOpenBankingSync, isOpenBankingStatement, obStatementMessage) => {
  const formatIsoDateToIt = (value) => {
    if (!value || typeof value !== 'string') return '';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const [y, m, d] = value.split('-');
    return `${d}/${m}/${y}`;
  };

  if (isMonitoring) {
    const info = job.info || {};
    if (info.kind === 'passp_fatture_print') {
      const fmtDate = (v) => {
        if (!v) return '-';
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return v;
        return d.toLocaleDateString('it-IT');
      };
      const amount = typeof info.totale === 'number' ? info.totale : 0;
      const formatCurrency = (value) =>
        new Intl.NumberFormat('it-IT', {
          style: 'currency',
          currency: 'EUR'
        }).format(value || 0);
      return (
        <>
          Data: {fmtDate(info.data_documento)} Numero: {info.serie_numero || '-'}
          <br />
          Cliente: {info.cliente || '-'}
          <br />
          Importo: {formatCurrency(amount)}
        </>
      );
    }

    if (info.kind === 'passp_client_statement') {
      return (
        <>
          Cliente: {info.clientName || '-'}
          {info.clientCode ? <> • Codice: {info.clientCode}</> : null}
        </>
      );
    }

    if (info.kind === 'modulistica_test_pdf') {
      return (
        <>
          Modello: {info.nomeModello || '-'}
          <br />
          Cliente: {info.customerName || '-'}
        </>
      );
    }

    const env = info.env || 'unknown';
    const legalIdVal = typeof info.legalId === 'number' ? info.legalId : null;
    const ref = info.referenceDate || '';
    const summaryNum = Number(info.summaryMs) || 0;
    const listNum = Number(info.listMs) || 0;
    const totalNum = Number(info.totalMs) || 0;
    const fmtSec = (v) => `${(v / 1000).toFixed(1)} s`;
    const fmtDate = (r) => {
      if (!r || typeof r !== 'string') return null;
      const m = r.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return r;
      const [, y, mo, d] = m;
      return `${d}/${mo}/${y}`;
    };
    return (
      <>
        Ambiente: {env}
        <br />
        Referente: {legalIdVal === 0 ? 'Tutti' : legalIdVal ?? '-'}
        <br />
        Riepilogo: {summaryNum} ms ({fmtSec(summaryNum)})
        <br />
        Lista: {listNum} ms ({fmtSec(listNum)})
        <br />
        Totale: {totalNum} ms ({fmtSec(totalNum)})
        <br />
        {ref ? <>Data: {fmtDate(ref)}</> : null}
      </>
    );
  }

  if (job.type === 'cloudtalk_sync') {
    return job.status === 'completed'
      ? 'Chiamate sincronizzate con successo.'
      : 'Si è verificato un errore durante la sincronizzazione.';
  }

  if (job.type === 'passp_fatture_sync') {
    if (job.status === 'completed') {
      return job.result?.message || 'Sincronizzazione Fatture Passpartout completata.';
    }
    return 'Errore durante la sincronizzazione Fatture Passpartout.';
  }

  if (job.type === 'passp_clienti_sync') {
    if (job.status === 'completed') {
      return job.result?.message || 'Sincronizzazione Clienti Passpartout completata.';
    }
    return 'Errore durante la sincronizzazione Clienti Passpartout.';
  }

  if (job.type === 'passp_conti_sync') {
    if (job.status === 'completed') {
      const azienda = job.result?.azienda || '';
      const anno = job.result?.anno || '';
      if (azienda && anno) {
        return `Sincronizzazione Conti di Bilancio - Azienda ${azienda} / ${anno}`;
      }
      return 'Sincronizzazione Conti di Bilancio completata.';
    }
    return 'Errore durante la sincronizzazione Conti di Bilancio.';
  }

  if (job.type === 'passp_prima_nota_sync') {
    if (job.status === 'completed') {
      return job.result?.message || 'Sincronizzazione Prima Nota Passpartout completata.';
    }
    return 'Errore durante la sincronizzazione Prima Nota Passpartout.';
  }

  if (job.type === 'funnelix_import_sync') {
    if (job.status === 'completed') {
      const imported = Number(job.result?.importati || 0);
      const read = Number(job.result?.letti || 0);
      return `Import completato: ${imported} contatti importati su ${read} letti.`;
    }
    return 'Errore durante import contatti Funnelix.';
  }

  if (job.type === 'open_banking_tx_csv_import') {
    if (job.status === 'completed') {
      const inserted = Number(job.result?.inserted || 0);
      const skipped = Number(job.result?.skipped || 0);
      return `Import completato: ${inserted} inserite, ${skipped} scartate.`;
    }
    return 'Errore durante importazione transazioni CSV.';
  }

  if (job.type === 'funnelix_verifica_clienti_csv') {
    if (job.status === 'completed') {
      const processed = Number(job.result?.processed || 0);
      const matched = Number(job.result?.matched || 0);
      const errors = Number(job.result?.errors || 0);
      return `Verifica completata: ${processed} contatti, trovati ${matched}, errori ${errors}.`;
    }
    return 'Errore durante verifica clienti Funnelix.';
  }

  if (job.type === 'funnelix_push_clienti') {
    if (job.status === 'completed') {
      const processed = Number(job.result?.processed || 0);
      const updated = Number(job.result?.updated || 0);
      const created = Number(job.result?.created || 0);
      const skipped = Number(job.result?.skippedAlreadyUpToDate || 0);
      const errors = Number(job.result?.errors || 0);
      const first = Array.isArray(job.result?.errorSamples) ? job.result.errorSamples[0] : null;
      const firstMsg = first?.responseSummary || first?.error || null;
      return `Aggiornamento completato: ${processed} contatti, aggiornate ${updated}, create ${created}, già aggiornati ${skipped}, errori ${errors}.${errors > 0 && firstMsg ? ` Primo errore: ${firstMsg}` : ''}`;
    }
    return 'Errore durante aggiornamento clienti su Funnelix.';
  }

  if (job.type === 'pf_clienti_assign') {
    if (job.status === 'completed') {
      return job.result?.message || 'Assegnazione clienti completata per Pratiche Finanziarie.';
    }
    return job.result?.message || "Errore durante l'assegnazione clienti per Pratiche Finanziarie.";
  }

  if (isOpenBankingSync) {
    if (job.status === 'completed') {
      return job.result?.message || 'Sincronizzazione Open Banking completata.';
    }
    return 'Errore durante la sincronizzazione Open Banking.';
  }

  if (isOpenBankingStatement) {
    if (job.status === 'completed') {
      return obStatementMessage || `Estratto conto banche periodo pronto (ID: ${job.id}).`;
    }
    return "Impossibile generare l'estratto conto banche.";
  }

  if (job.type === 'customer_csv') {
    if (job.status === 'completed') {
      return "L'esportazione richiesta è disponibile.";
    }
    return "Impossibile generare l'esportazione.";
  }

  if (job.type === 'cloudtalk_recall_csv') {
    if (job.status === 'completed') {
      return job.result?.message || 'Esportazione CSV per Cloudtalk (Dialer) completata.';
    }
    return "Impossibile generare l'esportazione CSV per Cloudtalk (Dialer).";
  }

  if (job.type === 'dossier_recall_csv') {
    if (job.status === 'completed') {
      return job.result?.message || 'Esportazione CSV Recall Preventivi completata.';
    }
    return "Impossibile generare l'esportazione CSV Recall Preventivi.";
  }

  if (job.type === 'job_notturno') {
    if (job.status === 'completed') {
      return job.result?.message || 'Esecuzione schedulata completata.';
    }
    return 'Errore durante l\'esecuzione del job notturno.';
  }

  if (job.type === 'cloudtalk_calls') {
    if (job.status === 'completed') {
      return job.result?.message || `Report CloudTalk pronto (ID: ${job.id}).`;
    }
    return 'Impossibile generare il report CloudTalk.';
  }

  if (job.type === 'scadenzario_import_csv') {
    if (job.status === 'completed') {
      const imported = Number(job.result?.imported || 0);
      const failed = Number(job.result?.failed || 0);
      const fileName = String(job.result?.originalFileName || job.meta?.originalFileName || '').trim();
      const base = `Import completato: ${imported} importati, ${failed} scartati.`;
      if (fileName) {
        return (
          <>
            File: {fileName}
            <br />
            {base}
          </>
        );
      }
      if (failed > 0) {
        return base;
      }
      return base;
    }
    return "Impossibile completare l'importazione piani.";
  }

  if (job.type === 'recessi_import_csv') {
    if (job.status === 'completed') {
      const imported = Number(job.result?.imported || 0);
      const failed = Number(job.result?.failed || 0);
      const total = Number(job.result?.total || 0);
      const base = `Import completato: ${imported} importati, ${failed} scartati${total ? ` su ${total}` : ''}.`;
      const samples = Array.isArray(job.result?.errorSamples) ? job.result.errorSamples : [];
      if (!failed || samples.length === 0) return base;
      return (
        <>
          {base}
          <br />
          {samples.slice(0, 3).map((e, idx) => (
            <span key={`${job.id}-err-${idx}`}>
              Riga {e.row}: {e.error}
              <br />
            </span>
          ))}
          {failed > 3 ? `Altri ${failed - 3} errori.` : null}
        </>
      );
    }
    return job.result?.message || 'Impossibile completare l’import CSV recessi.';
  }

  if (job.type === 'invii_modulistica_bulk_send_email') {
    if (job.status === 'completed') {
      const sent = Number(job.result?.sent || 0);
      const failed = Number(job.result?.failed || 0);
      const total = Number(job.result?.total || 0);
      const fromEmail = String(job.result?.email_mittente || '').trim();
      const base = `Invio completato: ${sent} inviate, ${failed} errori${total ? ` su ${total}` : ''}.`;
      if (fromEmail) return `${base} Mittente: ${fromEmail}.`;
      return base;
    }
    return job.result?.message || 'Impossibile completare l’invio email promemoria.';
  }

  if (job.type === 'scadenzario_performance_referenti_pdf') {
    if (job.status === 'completed') {
      return 'Il report Performance Referenti è pronto per il download.';
    }
    return 'Impossibile generare il report Performance Referenti.';
  }

  if (job.type === 'kpi_lead_yearly_pdf') {
    if (job.status === 'completed') {
      return job.result?.message
        ? `${job.result.message}.`
        : 'Il report KPI Lead Annuale è pronto per il download.';
    }
    return 'Impossibile generare il report KPI Lead Annuale.';
  }

  if (job.type === 'hr_absence_report') {
    if (job.status === 'completed') {
      return job.result?.message || `Report assenze pronto (ID: ${job.id}).`;
    }
    return 'Impossibile generare il report assenze.';
  }

  if (job.type === 'hr_time_variance_report') {
    if (job.status === 'completed') {
      return job.result?.message || `Report Anticipi/Posticipi pronto (ID: ${job.id}).`;
    }
    return 'Impossibile generare il report Anticipi/Posticipi.';
  }

  if (job.type === 'blacklist_segnalazioni_report') {
    if (job.status === 'completed') {
      return job.result?.message || `Report Segnalazioni Blacklist pronto (ID: ${job.id}).`;
    }
    return 'Impossibile generare il report Segnalazioni Blacklist.';
  }

  if (job.type === 'pratiche_monitoraggio') {
    if (job.status === 'completed') {
      return job.result?.message || `Report monitoraggio pratiche pronto (ID: ${job.id}).`;
    }
    return 'Impossibile generare il report monitoraggio pratiche.';
  }

  if (job.type === 'firme_report') {
    if (job.status === 'completed') {
      return job.result?.message || `Report firme pronto (ID: ${job.id}).`;
    }
    return 'Impossibile generare il report firme.';
  }

  if (job.type === 'firme_csv_report') {
    if (job.status === 'completed') {
      return job.result?.message || `Esportazione CSV firme pronta (ID: ${job.id}).`;
    }
    return "Impossibile generare l'esportazione CSV firme.";
  }

  if (job.type === 'legali_chiusure_report') {
    if (job.status === 'completed') {
      return job.result?.message || `Report legali chiusure pronto (ID: ${job.id}).`;
    }
    return 'Impossibile generare il report legali chiusure.';
  }

  if (job.type === 'legali_chiusure_csv_report') {
    if (job.status === 'completed') {
      return job.result?.message || `Esportazione CSV legali chiusure pronta (ID: ${job.id}).`;
    }
    return "Impossibile generare l'esportazione CSV legali chiusure.";
  }

  if (job.type === 'pratiche_uo_report') {
    if (job.status === 'completed') {
      return job.result?.message || `Report Pratiche U/O pronto (ID: ${job.id}).`;
    }
    return 'Impossibile generare il report Pratiche U/O.';
  }

  if (job.type === 'passp_posizione_contabile_report') {
    if (job.status === 'completed') {
      return job.result?.message || `Report posizione contabile pronto (ID: ${job.id}).`;
    }
    return 'Impossibile generare il report posizione contabile.';
  }

  if (job.type === 'passp_fatture_bulk_send_email') {
    const to = String(job.result?.to || job.meta?.to || '').trim();
    const count = Number(job.result?.count ?? job.meta?.count ?? 0);
    const cliente = String(job.result?.cliente || job.meta?.cliente || '').trim();
    if (job.status === 'completed') {
      const parts = [];
      if (cliente) parts.push(`Cliente: ${cliente}`);
      if (to) parts.push(`A: ${to}`);
      if (count > 0) parts.push(`Documenti: ${count}`);
      if (parts.length > 0) return `${parts.join(' · ')}.`;
      return 'Invio completato.';
    }
    const reason = String(job.error || '').trim();
    return reason ? `Errore: ${reason}` : 'Impossibile inviare email documenti.';
  }

  if (job.type === 'admin_activity_summary') {
    if (job.status === 'completed') {
      const userLabel = String(job.meta?.userLabel || '').trim();
      const fromIt = String(job.meta?.dateFromIt || '').trim() || formatIsoDateToIt(job.meta?.dateFrom);
      const toIt = String(job.meta?.dateToIt || '').trim() || formatIsoDateToIt(job.meta?.dateTo);
      const parts = [];
      if (userLabel) parts.push(`Utente: ${userLabel}`);
      if (fromIt && toIt) parts.push(`Periodo: ${fromIt} - ${toIt}`);
      if (parts.length > 0) return `${parts.join(' · ')}.`;
      return `Report attività pronto (ID: ${job.id}).`;
    }
    return 'Impossibile generare il report attività.';
  }

  if (typeof job.result?.fileName === 'string' && /\.csv$/i.test(job.result.fileName)) {
    if (job.status === 'completed') {
      return job.result?.message || `Esportazione CSV pronta (ID: ${job.id}).`;
    }
    return "Impossibile generare l'esportazione CSV.";
  }

  if (job.status === 'completed') {
    return job.result?.message || `Il report #${job.id} è pronto per il download.`;
  }
  return `Impossibile generare il report #${job.id}.`;
};

export function Topbar({ onMenuClick }) {
  const { user, logout } = useAuth()
  const { jobs, markAsRead, markAllAsRead, downloadJob, removeJob } = useJob()
  const [showNotifications, setShowNotifications] = useState(false)
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [showLanguageMenu, setShowLanguageMenu] = useState(false)
  const [showAiMenu, setShowAiMenu] = useState(false)
  const [showSupportDialog, setShowSupportDialog] = useState(false)
  const [onlineUsersDialogOpen, setOnlineUsersDialogOpen] = useState(false)
  const [onlineUsersLoading, setOnlineUsersLoading] = useState(false)
  const [onlineUsers, setOnlineUsers] = useState([])
  const [onlineUsersSearch, setOnlineUsersSearch] = useState('')
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0)
  const [messagesDialogOpen, setMessagesDialogOpen] = useState(false)
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [messages, setMessages] = useState([])
  const [sendMessageDialogOpen, setSendMessageDialogOpen] = useState(false)
  const [sendMessageTarget, setSendMessageTarget] = useState(null)
  const [sendMessageText, setSendMessageText] = useState('')
  const [sendingMessage, setSendingMessage] = useState(false)
  const [confirmActionState, setConfirmActionState] = useState({ open: false, target: '', title: '', message: '' })
  const notificationRef = useRef(null)
  const profileRef = useRef(null)
  const languageRef = useRef(null)
  const aiMenuRef = useRef(null)
  const { i18n } = useTranslation()
  const navigate = useNavigate()

  const languages = {
    'it': { flag: '🇮🇹', name: 'Italiano' },
    'en': { flag: '🇬🇧', name: 'English' },
    'es-ES': { flag: '🇪🇸', name: 'Español' },
    'es-MX': { flag: '🇲🇽', name: 'Español (MX)' }
  };

  const currentLang = languages[i18n.language] || languages['it'];

  // Filter for relevant notifications (completed or failed jobs)
  const notifications = jobs.filter(j => j.status === 'completed' || j.status === 'failed')
  const unreadCount = notifications.filter(j => !j.read).length
  const hasNotifications = notifications.length > 0
  const roleName = (user?.role_name || user?.role || "").toLowerCase()
  const isAdmin = roleName === "admin"
  const isAgency = roleName === "agenzia"
  const { enabled: maintenanceEnabled } = useMaintenance()

  const formatTimeOnly = (value) => {
    if (!value) return '-'
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return '-'
    return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
  }

  const openOnlineUsers = async () => {
    setOnlineUsersDialogOpen(true)
    try {
      setOnlineUsersLoading(true)
      const res = await api.get('/presence/online', { params: { minutes: 5 } })
      const rows = Array.isArray(res.data?.data) ? res.data.data : []
      const filtered = rows.filter((u) => Number(u?.id) !== Number(user?.id))
      setOnlineUsers(filtered)
      setOnlineUsersSearch('')
    } catch (e) {
      toast.error('Errore caricamento utenti online')
    } finally {
      setOnlineUsersLoading(false)
    }
  }

  const refreshUnreadMessagesCount = async () => {
    try {
      const res = await api.get('/messages/unread/count')
      const count = Number(res.data?.count || 0)
      setUnreadMessagesCount(Number.isFinite(count) ? count : 0)
    } catch (_) {}
  }

  const openMessages = async () => {
    setMessagesDialogOpen(true)
    try {
      setMessagesLoading(true)
      const res = await api.get('/messages/unread/consume', { params: { limit: 50 } })
      const rows = Array.isArray(res.data?.data) ? res.data.data : []
      setMessages(rows)
      setUnreadMessagesCount(0)
    } catch (e) {
      toast.error('Errore caricamento messaggi')
    } finally {
      setMessagesLoading(false)
    }
  }

  const openConfirmAction = (target) => {
    const t = String(target || '').trim()
    if (!t) return
    const configs = {
      '/admin/settings': {
        title: 'Conferma Manutenzione',
        message:
          'Stai per aprire le impostazioni di manutenzione. Questa funzione può limitare l’accesso al sistema per gli utenti non autorizzati.\n\nAssicurati di avere un motivo legittimo e l’autorizzazione necessaria, in conformità alla normativa privacy e alle policy aziendali.'
      },
      '/db-monitor': {
        title: 'Conferma Monitoraggio DB',
        message:
          'Stai per accedere al monitoraggio del database. Questa sezione può mostrare informazioni sensibili (stato del DB, metriche e log tecnici).\n\nAssicurati di avere un motivo legittimo e l’autorizzazione necessaria, in conformità alla normativa privacy e alle policy aziendali.'
      }
    }
    const cfg = configs[t] || {
      title: 'Conferma',
      message:
        'Stai per accedere a una sezione riservata. Assicurati di avere un motivo legittimo e l’autorizzazione necessaria.'
    }
    setShowProfileMenu(false)
    setConfirmActionState({ open: true, target: t, title: cfg.title, message: cfg.message })
  }

  const closeConfirmAction = () => {
    setConfirmActionState({ open: false, target: '', title: '', message: '' })
  }

  const confirmAction = () => {
    const target = String(confirmActionState?.target || '').trim()
    closeConfirmAction()
    if (target) {
      navigate(target)
    }
  }

  const filteredOnlineUsers = useMemo(() => {
    const term = String(onlineUsersSearch || '').trim().toLowerCase()
    if (!term) return onlineUsers
    return (onlineUsers || []).filter((u) => {
      const name = String(u?.name || '').toLowerCase()
      const email = String(u?.email || '').toLowerCase()
      return name.includes(term) || email.includes(term)
    })
  }, [onlineUsers, onlineUsersSearch])

  const startSendMessage = (target) => {
    setSendMessageTarget(target)
    setSendMessageText('')
    setSendMessageDialogOpen(true)
  }

  const doSendMessage = async () => {
    const toUserId = sendMessageTarget?.id
    const text = String(sendMessageText || '').trim()
    if (!toUserId) return
    if (!text) {
      toast.error('Messaggio vuoto')
      return
    }
    try {
      setSendingMessage(true)
      await api.post('/messages', { to_user_id: toUserId, message: text })
      toast.success('Messaggio inviato')
      setSendMessageDialogOpen(false)
      setSendMessageTarget(null)
      setSendMessageText('')
    } catch (e) {
      const msg = e?.response?.data?.message || 'Errore invio messaggio'
      toast.error(msg)
    } finally {
      setSendingMessage(false)
    }
  }

  useEffect(() => {
    if (!user?.id) return
    let pingTimer = null
    let msgTimer = null

    const ping = async () => {
      try {
        await api.post('/presence/ping')
      } catch (_) {}
    }

    ping()
    refreshUnreadMessagesCount()
    pingTimer = window.setInterval(ping, 45000)
    msgTimer = window.setInterval(refreshUnreadMessagesCount, 15000)

    return () => {
      if (pingTimer) window.clearInterval(pingTimer)
      if (msgTimer) window.clearInterval(msgTimer)
    }
  }, [user?.id])

  // Close notifications when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false)
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false)
      }
      if (languageRef.current && !languageRef.current.contains(event.target)) {
        setShowLanguageMenu(false)
      }
      if (aiMenuRef.current && !aiMenuRef.current.contains(event.target)) {
        setShowAiMenu(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleDownload = async (e, jobId) => {
    e.stopPropagation()
    await downloadJob(jobId)
  }

  const handleDownloadPrint = async (e, info) => {
    e.stopPropagation()
    if (!info?.docId) return
    try {
      const res = await api.post(
        `/passp-fatture/${info.docId}/print`,
        { sendEmail: false },
        { responseType: 'blob' }
      )
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `documento_${info.sigla || ''}_${info.serie || ''}_${info.numero || ''}.pdf`
      )
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download documento Passpartout error:', error)
      toast.error('Errore durante il download del documento')
    }
  }

  const handleDownloadPasspStatement = async (e, info) => {
    e.stopPropagation()
    if (!info?.clientId) return
    try {
      const res = await api.get(`/passp-clientes/${info.clientId}/statement-pdf`, {
        responseType: 'blob'
      })
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const clientSlug = (info.clientName || '').replace(/\s+/g, '_') || `cliente_${info.clientId}`
      link.setAttribute('download', `estratto_conto_${clientSlug}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download estratto conto cliente error:', error)
      toast.error('Errore durante il download dell’estratto conto')
    }
  }

  const handleDownloadModulisticaTestPdf = async (e, info) => {
    e.stopPropagation()
    if (!info?.customerId || !info?.content) return
    try {
      const res = await api.post(
        '/modulistica/test-render/pdf',
        {
          content: String(info.content || ''),
          customer_id: Number(info.customerId),
          nome_modello: String(info.nomeModello || '')
        },
        { responseType: 'blob' }
      )
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const disposition = res.headers?.['content-disposition'] || ''
      const match = String(disposition).match(/filename\s*=\s*"([^"]+)"/i) || String(disposition).match(/filename\s*=\s*([^;]+)/i)
      const headerFilename = match && match[1] ? String(match[1]).trim() : null
      link.setAttribute('download', headerFilename || info.filename || 'test_modello.pdf')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download modulistica test pdf error:', error)
      toast.error('Errore durante il download del PDF di test')
    }
  }

  const handleDismiss = (e, jobId) => {
    e.stopPropagation()
    removeJob(jobId)
  }

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications)
    if (!showNotifications && unreadCount > 0) {
      // Optional: mark all as read when opening? 
      // Or keep them unread until interaction?
      // Let's keep them unread until manually dismissed or clicked
    }
  }

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng)
    setShowLanguageMenu(false)
  }

  const handleSupportClick = () => {
    setShowSupportDialog(true)
  }

  const confirmSupportRedirect = () => {
    window.open("https://assistenza.digital-pmi.it", "_blank")
    setShowSupportDialog(false)
  }

  return (
    <header className="h-16 w-full max-w-full border-b bg-card flex items-center justify-between px-4 sticky top-0 z-20">
      <div className="flex min-w-0 items-center gap-4">
        <Button variant="ghost" size="icon" className="xl:hidden" onClick={onMenuClick}>
          <Menu className="h-5 w-5" />
        </Button>
        <div className="relative w-64 hidden md:block">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input 
            type="search" 
            placeholder="Cerca cliente o pratica..." 
            className="pl-9 bg-secondary/50 border-transparent focus:bg-background focus:border-input transition-colors"
          />
        </div>
      </div>

      <div className="flex min-w-0 items-center gap-3 md:gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-primary animate-pulse"
          onClick={handleSupportClick}
          title="Assistenza"
        >
          <Headset className="h-5 w-5" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-primary animate-pulse"
          onClick={() => navigate("/tasks")}
          title="Task"
        >
          <CheckSquare className="h-5 w-5" />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="relative text-muted-foreground hover:text-primary"
          onClick={openMessages}
          title="Messaggi"
        >
          <MessageCircle className="h-5 w-5" />
          {unreadMessagesCount > 0 && (
            <span
              className="absolute -top-1 -right-1 min-h-[1.1rem] min-w-[1.1rem] rounded-full bg-destructive text-[10px] leading-none text-white flex items-center justify-center px-1 ring-2 ring-card"
            >
              {unreadMessagesCount > 99 ? "99+" : unreadMessagesCount}
            </span>
          )}
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-primary"
          onClick={openOnlineUsers}
          title="Utenti online"
        >
          <Users className="h-5 w-5" />
        </Button>

        <ThemeSwitcher />

        {isAdmin && (
          <>
            <div className="relative" ref={aiMenuRef}>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-primary px-2 text-xs gap-1"
                onClick={() => setShowAiMenu(!showAiMenu)}
                title="Sezione AI (settings, prompt, consumi, log)"
              >
                <Sparkles className="h-4 w-4" />
                <span className="hidden sm:inline-block">AI</span>
              </Button>
              {showAiMenu && (
                <Card className="absolute right-0 top-full mt-2 w-64 z-50 shadow-lg border-border p-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start gap-2 text-xs"
                    onClick={() => {
                      setShowAiMenu(false)
                      navigate("/ai/settings")
                    }}
                  >
                    <Shield className="h-4 w-4" />
                    <div className="flex flex-col items-start">
                      <span>AI Settings</span>
                      <span className="text-[10px] text-muted-foreground">Modelli e chiavi provider</span>
                    </div>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start gap-2 text-xs"
                    onClick={() => {
                      setShowAiMenu(false)
                      navigate("/ai/llm-models")
                    }}
                  >
                    <Database className="h-4 w-4" />
                    <div className="flex flex-col items-start">
                      <span>Modelli LLM</span>
                      <span className="text-[10px] text-muted-foreground">Catalogo, capacità e prezzi</span>
                    </div>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start gap-2 text-xs"
                    onClick={() => {
                      setShowAiMenu(false)
                      navigate("/ai/conversations-admin")
                    }}
                  >
                    <Sparkles className="h-4 w-4" />
                    <div className="flex flex-col items-start">
                      <span>Modelli conversazioni</span>
                      <span className="text-[10px] text-muted-foreground">Override modello per singola chat</span>
                    </div>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start gap-2 text-xs"
                    onClick={() => {
                      setShowAiMenu(false)
                      navigate("/ai/prompts")
                    }}
                  >
                    <FileText className="h-4 w-4" />
                    <div className="flex flex-col items-start">
                      <span>AI Prompt</span>
                      <span className="text-[10px] text-muted-foreground">Prompt suggeriti dalla dashboard</span>
                    </div>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start gap-2 text-xs"
                    onClick={() => {
                      setShowAiMenu(false)
                      navigate("/ai/usage")
                    }}
                  >
                    <BarChart3 className="h-4 w-4" />
                    <div className="flex flex-col items-start">
                      <span>Consumi AI</span>
                      <span className="text-[10px] text-muted-foreground">Token per utente e modello</span>
                    </div>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start gap-2 text-xs"
                    onClick={() => {
                      setShowAiMenu(false)
                      navigate("/ai/logs")
                    }}
                  >
                    <Mail className="h-4 w-4" />
                    <div className="flex flex-col items-start">
                      <span>AI Log</span>
                      <span className="text-[10px] text-muted-foreground">Dettaglio richieste e prompt</span>
                    </div>
                  </Button>
                </Card>
              )}
            </div>
          </>
        )}

        <div className="relative" ref={languageRef}>
          <Button variant="ghost" size="sm" className="gap-2" onClick={() => setShowLanguageMenu(!showLanguageMenu)}>
            <span className="text-lg leading-none">{currentLang.flag}</span>
            <span className="hidden sm:inline-block font-medium">{currentLang.name}</span>
          </Button>
          {showLanguageMenu && (
            <Card className="absolute right-0 top-full mt-2 w-48 z-50 shadow-lg border-border p-1">
              {Object.entries(languages).map(([code, lang]) => (
                <Button 
                  key={code}
                  variant="ghost" 
                  size="sm" 
                  className={cn("w-full justify-start gap-2", i18n.language === code && "bg-muted")} 
                  onClick={() => changeLanguage(code)}
                >
                  <span className="text-lg leading-none">{lang.flag}</span>
                  <span>{lang.name}</span>
                </Button>
              ))}
            </Card>
          )}
        </div>

        <div className="relative" ref={notificationRef}>
          <Button 
            variant="ghost" 
            size="icon" 
            className="relative"
            onClick={toggleNotifications}
            title="Notifiche"
          >
            <Bell className="h-5 w-5 text-muted-foreground" />
            {unreadCount > 0 && (
              <span
                className="absolute -top-1 -right-1 min-h-[1.1rem] min-w-[1.1rem] rounded-full bg-destructive text-[10px] leading-none text-white flex items-center justify-center px-1 ring-2 ring-card"
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </Button>

          {showNotifications && (
            <Card className="absolute right-0 top-full mt-2 w-80 sm:w-96 z-50 shadow-lg border-border animate-in fade-in slide-in-from-top-2">
              <div className="flex items-center justify-between p-4 border-b">
                <h4 className="font-semibold text-sm">Notifiche</h4>
                {unreadCount > 0 && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs h-8 text-muted-foreground hover:text-primary"
                    onClick={markAllAsRead}
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Segna tutte come lette
                  </Button>
                )}
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                {hasNotifications ? (
                  <div className="divide-y">
                    {notifications.map((job) => {
                      const isMonitoring = job.type === 'monitoring'
                      const isScadenzarioImport = job.type === 'scadenzario_import_csv'
                      const hasObSyncShape =
                        job.result &&
                        typeof job.result.accounts === 'number' &&
                        typeof job.result.created === 'number' &&
                        typeof job.result.updated === 'number'
                      const isOpenBankingSync = job.type === 'open_banking_tx_sync' || hasObSyncShape
                      const isOpenBankingStatement = job.type === 'open_banking_statement'
                      const isCsvDownload =
                        job.type === 'customer_csv' ||
                        job.type === 'cloudtalk_recall_csv' ||
                        job.type === 'dossier_recall_csv' ||
                        job.type === 'funnelix_verifica_clienti_csv' ||
                        job.type === 'legali_chiusure_csv_report' ||
                        (typeof job.result?.fileName === 'string' && /\.csv$/i.test(job.result.fileName)) ||
                        (typeof job.result?.filename === 'string' && /\.csv$/i.test(job.result.filename)) ||
                        (isOpenBankingStatement && job.result?.filename && /\.csv$/i.test(job.result.filename))
                      const obStatementMessage =
                        isOpenBankingStatement && job.result && typeof job.result.message === 'string'
                          ? job.result.message
                          : null
                      return (
                      <div 
                        key={job.id} 
                        className={cn(
                          "p-4 hover:bg-muted/50 transition-colors flex items-start gap-3",
                          !job.read && "bg-muted/20"
                        )}
                        onClick={() => markAsRead(job.id)}
                      >
                        <div className={cn(
                          "h-8 w-8 rounded-full flex items-center justify-center shrink-0",
                          job.status === 'completed'
                            ? (isMonitoring
                                ? "bg-sky-100 text-sky-600"
                                : job.type === 'cloudtalk_sync'
                                  ? "bg-purple-100 text-purple-600"
                                  : job.type === 'passp_clienti_sync'
                                    ? "bg-emerald-100 text-emerald-600"
                                : job.type === 'passp_fatture_bulk_send_email'
                                  ? "bg-emerald-100 text-emerald-600"
                                    : isOpenBankingSync
                                      ? "bg-blue-100 text-blue-600"
                                      : "bg-green-100 text-green-600")
                            : "bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-300"
                        )}>
                          {job.status === 'completed'
                            ? (isMonitoring
                                ? <ListTodo className="h-4 w-4" />
                                : job.type === 'cloudtalk_sync'
                                  ? <PhoneCall className="h-4 w-4" />
                                  : job.type === 'passp_clienti_sync'
                                    ? <Users className="h-4 w-4" />
                                : job.type === 'passp_fatture_bulk_send_email'
                                  ? <Mail className="h-4 w-4" />
                                    : job.type === 'funnelix_import_sync' || job.type === 'funnelix_verifica_clienti_csv'
                                      ? <Users className="h-4 w-4" />
                                    : job.type === 'funnelix_push_clienti'
                                      ? <Briefcase className="h-4 w-4" />
                                    : <FileText className="h-4 w-4" />)
                            : <X className="h-4 w-4" />}
                        </div>
                        <div className="flex-1 space-y-1">
                          <p className="text-sm font-medium leading-none">
                            {getJobTitle(job, isMonitoring, isOpenBankingSync, isOpenBankingStatement, obStatementMessage)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {getJobSubtitle(job, isMonitoring, isOpenBankingSync, isOpenBankingStatement, obStatementMessage)}
                          </p>
                          <p className="text-[10px] text-muted-foreground pt-1">
                            {new Date(job.createdAt).toLocaleTimeString()}
                          </p>
                          {job.status === 'completed' &&
                            !isMonitoring &&
                            job.type !== 'cloudtalk_sync' &&
                            job.type !== 'passp_fatture_sync' &&
                            job.type !== 'passp_clienti_sync' &&
                            job.type !== 'passp_conti_sync' &&
                            job.type !== 'passp_prima_nota_sync' &&
                            job.type !== 'open_banking_tx_csv_import' &&
                            job.type !== 'job_notturno' &&
                            job.type !== 'funnelix_push_clienti' &&
                            job.type !== 'recessi_import_csv' &&
                            job.type !== 'invii_modulistica_bulk_send_email' &&
                            job.type !== 'passp_fatture_bulk_send_email' &&
                            job.type !== 'pf_clienti_assign' &&
                            !isOpenBankingSync && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="h-7 text-xs mt-2 w-full"
                              onClick={(e) => handleDownload(e, job.id)}
                            >
                              <Download className="h-3 w-3 mr-2" />
                              {isScadenzarioImport
                                ? 'Scarica TXT esito'
                                : isCsvDownload
                                ? 'Scarica CSV'
                                : job.type === 'scadenzario_performance_referenti_pdf'
                                ? 'Scarica report Performance'
                                : job.type === 'admin_activity_summary'
                                ? 'Scarica report attività'
                                : 'Scarica PDF'}
                            </Button>
                          )}
                          {isMonitoring && job.status === 'completed' && job.info?.kind === 'passp_fatture_print' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs mt-2 w-full"
                              onClick={(e) => handleDownloadPrint(e, job.info)}
                            >
                              <Download className="h-3 w-3 mr-2" />
                              Scarica PDF
                            </Button>
                          )}
                          {isMonitoring && job.status === 'completed' && job.info?.kind === 'passp_client_statement' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs mt-2 w-full"
                              onClick={(e) => handleDownloadPasspStatement(e, job.info)}
                            >
                              <Download className="h-3 w-3 mr-2" />
                              {job.info?.clientName
                                ? `Estratto conto ${job.info.clientName}`
                                : 'Scarica estratto conto'}
                            </Button>
                          )}
                          {isMonitoring && job.status === 'completed' && job.info?.kind === 'modulistica_test_pdf' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs mt-2 w-full"
                              onClick={(e) => handleDownloadModulisticaTestPdf(e, job.info)}
                            >
                              <Download className="h-3 w-3 mr-2" />
                              Scarica PDF
                            </Button>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 -mr-2 -mt-2 text-muted-foreground hover:text-foreground"
                          onClick={(e) => handleDismiss(e, job.id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    )}).reverse()}
                  </div>
                ) : (
                  <div className="p-8 text-center text-muted-foreground">
                    <Bell className="h-8 w-8 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">Nessuna notifica</p>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        <div className="relative border-l pl-4 shrink-0" ref={profileRef}>
          <Button 
            variant="ghost" 
            className="flex items-center gap-2 p-1 hover:bg-muted/50 rounded-full h-auto"
            onClick={() => setShowProfileMenu(!showProfileMenu)}
          >
            <span className="text-xs font-medium md:hidden">Account</span>
            <div className="text-right hidden md:block mr-1 max-w-[120px]">
                <p className="text-sm font-medium leading-none truncate">{user?.name || "Admin User"}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.role || user?.role_name || "Admin"}</p>
            </div>
            <Avatar className="h-9 w-9 border-2 border-background ring-1 ring-border">
                <AvatarImage src="" alt={user?.name} />
                <AvatarFallback>{user?.name?.substring(0, 2).toUpperCase() || "DB"}</AvatarFallback>
            </Avatar>
          </Button>

          {showProfileMenu && (
            <Card className="fixed right-2 top-16 mt-2 w-[min(14rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)] z-[70] shadow-lg border-border animate-in fade-in slide-in-from-top-2 p-1 md:absolute md:right-0 md:top-full md:mt-2">
              <div className="px-2 py-1.5 text-sm font-semibold border-b mb-1">
                Account
              </div>
              <div className="grid gap-1 mb-1 border-b pb-1">
                <Link to="/profile" onClick={() => setShowProfileMenu(false)}>
                  <Button variant="ghost" size="sm" className="w-full justify-start font-normal">
                    <User className="mr-2 h-4 w-4" />
                    Il mio profilo
                  </Button>
                </Link>
              </div>
              <div className="px-2 py-1.5 text-sm font-semibold border-b mb-1">
                Gestione
              </div>
              <div className="grid gap-1">
                {!isAgency && (
                  <Link to="/jobs" onClick={() => setShowProfileMenu(false)}>
                    <Button variant="ghost" size="sm" className="w-full justify-start font-normal">
                      <ListTodo className="mr-2 h-4 w-4" />
                      Job di sistema
                    </Button>
                  </Link>
                )}
                {!isAgency && (
                  <Link to="/email-accounts" onClick={() => setShowProfileMenu(false)}>
                    <Button variant="ghost" size="sm" className="w-full justify-start font-normal">
                      <Mail className="mr-2 h-4 w-4" />
                      Account email
                    </Button>
                  </Link>
                )}
                {isAdmin && (
                  <>
                    <Link to="/users" onClick={() => setShowProfileMenu(false)}>
                      <Button variant="ghost" size="sm" className="w-full justify-start font-normal">
                        <Users className="mr-2 h-4 w-4" />
                        Utenti
                      </Button>
                    </Link>
                    <Link to="/roles" onClick={() => setShowProfileMenu(false)}>
                      <Button variant="ghost" size="sm" className="w-full justify-start font-normal">
                        <Shield className="mr-2 h-4 w-4" />
                        Ruoli
                      </Button>
                    </Link>
                    <Link to="/app-departments" onClick={() => setShowProfileMenu(false)}>
                      <Button variant="ghost" size="sm" className="w-full justify-start font-normal">
                        <Briefcase className="mr-2 h-4 w-4" />
                        Dipartimenti
                      </Button>
                    </Link>
                    <Link to="/app-offices" onClick={() => setShowProfileMenu(false)}>
                      <Button variant="ghost" size="sm" className="w-full justify-start font-normal">
                        <Building2 className="mr-2 h-4 w-4" />
                        Uffici
                      </Button>
                    </Link>
                  </>
                )}
                <Link to="/data-protection" onClick={() => setShowProfileMenu(false)}>
                  <Button variant="ghost" size="sm" className="w-full justify-start font-normal">
                    <Shield className="mr-2 h-4 w-4" />
                    Policy protezione dati
                  </Button>
                </Link>
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start font-normal"
                    title="Impostazioni manutenzione"
                    onClick={() => openConfirmAction('/admin/settings')}
                  >
                    <span
                      className={cn(
                        "mr-2 inline-flex h-7 w-7 items-center justify-center rounded-full border",
                        maintenanceEnabled
                          ? "bg-amber-100 text-amber-700 border-amber-200"
                          : "bg-secondary text-muted-foreground border-border"
                      )}
                    >
                      <Wrench className="h-4 w-4" />
                    </span>
                    <span className="flex items-center gap-2">
                      Manutenzione
                      <Badge variant={maintenanceEnabled ? "warning" : "secondary"}>
                        {maintenanceEnabled ? "ON" : "OFF"}
                      </Badge>
                    </span>
                  </Button>
                )}
                {isAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start font-normal"
                    onClick={() => openConfirmAction('/db-monitor')}
                  >
                    <Database className="mr-2 h-4 w-4" />
                    Monitoraggio DB
                  </Button>
                )}
                <div className="border-t my-1"></div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full justify-start font-normal text-destructive dark:text-red-400 hover:text-destructive dark:hover:text-red-300 hover:bg-destructive/10 dark:hover:bg-red-500/15"
                  onClick={logout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>
      {/* Support Dialog */}
      <Dialog
        open={confirmActionState.open}
        onClose={closeConfirmAction}
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: 8
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontWeight: 'bold' }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: '#fee2e2',
              color: '#b91c1c',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <AlertTriangle size={18} />
          </Box>
          {confirmActionState.title}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {confirmActionState.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="ghost" onClick={closeConfirmAction}>
            Annulla
          </Button>
          <Button onClick={confirmAction}>
            Conferma
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={messagesDialogOpen}
        onClose={() => {
          if (messagesLoading) return
          setMessagesDialogOpen(false)
        }}
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: 8
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Messaggi</DialogTitle>
        <DialogContent>
          {messagesLoading ? (
            <Typography variant="body2" color="text.secondary">
              Caricamento...
            </Typography>
          ) : messages.length ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {messages.map((m) => (
                <Box
                  key={m.id}
                  sx={{
                    p: 1.25,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {m.from_user_name || m.from_user_email || 'Messaggio'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatTimeOnly(m.created_at)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                    {m.message_text}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nessun messaggio non letto.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outline"
            onClick={() => {
              setMessagesDialogOpen(false)
              setMessages([])
              refreshUnreadMessagesCount()
            }}
          >
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={onlineUsersDialogOpen}
        onClose={() => {
          if (onlineUsersLoading) return
          setOnlineUsersDialogOpen(false)
        }}
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: 8
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <span>Utenti online</span>
            <Badge
              variant="secondary"
              className="gap-1 border border-blue-100 bg-blue-50 text-blue-700"
              title={`Totale utenti online: ${onlineUsers.length}`}
            >
              <Users className="h-3.5 w-3.5" />
              <span className="tabular-nums">{onlineUsers.length}</span>
            </Badge>
          </Box>
        </DialogTitle>
        <DialogContent>
          {onlineUsersLoading ? (
            <Typography variant="body2" color="text.secondary">
              Caricamento...
            </Typography>
          ) : onlineUsers.length ? (
            <>
              <div className="relative mb-3">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={onlineUsersSearch}
                  onChange={(e) => setOnlineUsersSearch(e.target.value)}
                  placeholder="Cerca per nome o email..."
                  className="pl-9"
                />
              </div>
              {String(onlineUsersSearch || '').trim() ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Mostrati: {filteredOnlineUsers.length} / {onlineUsers.length}
                </Typography>
              ) : null}
              <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
                {filteredOnlineUsers.length ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {filteredOnlineUsers.map((u, idx) => (
                      <Box
                        key={u.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1.5,
                          py: 1,
                          px: 0.75,
                          borderTop: idx === 0 ? 'none' : '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: '#22c55e',
                              flex: '0 0 auto'
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                              {u.name || u.email}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {u.email ? u.email : ''}
                              {u.department_name ? ` • ${u.department_name}` : ''}
                              {u.last_seen_at ? ` • ${formatTimeOnly(u.last_seen_at)}` : ''}
                            </Typography>
                          </Box>
                        </Box>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-muted-foreground hover:text-primary"
                          onClick={() => startSendMessage(u)}
                          title="Invia messaggio"
                        >
                          <Mail className="h-4 w-4" />
                        </Button>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nessun risultato.
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nessun utente online negli ultimi 5 minuti.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outline" onClick={() => setOnlineUsersDialogOpen(false)}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sendMessageDialogOpen}
        onClose={() => {
          if (sendingMessage) return
          setSendMessageDialogOpen(false)
        }}
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: 8
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Messaggio{sendMessageTarget?.name ? ` a ${sendMessageTarget.name}` : ''}
        </DialogTitle>
        <DialogContent>
          <div className="grid gap-2">
            <textarea
              value={sendMessageText}
              onChange={(e) => setSendMessageText(e.target.value)}
              rows={4}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder="Scrivi un messaggio..."
              maxLength={1500}
            />
            <Typography variant="caption" color="text.secondary">
              {String(sendMessageText || '').length}/1500
            </Typography>
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outline"
            onClick={() => {
              setSendMessageDialogOpen(false)
              setSendMessageTarget(null)
              setSendMessageText('')
            }}
            disabled={sendingMessage}
          >
            Annulla
          </Button>
          <Button onClick={doSendMessage} disabled={sendingMessage}>
            {sendingMessage ? 'Invio...' : 'Invia'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={showSupportDialog} 
        onClose={() => setShowSupportDialog(false)}
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: 8
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Assistenza</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Vuoi aprire un ticket di assistenza sul portale Digital PMI?
          </Typography>
          <div className="flex items-center space-x-3 py-2">
             <div className="bg-blue-50 p-3 rounded-full flex items-center justify-center">
               <Headset className="h-6 w-6 text-blue-500" />
             </div>
             <Typography variant="caption" color="text.secondary">
               Verrai reindirizzato al portale di supporto esterno in una nuova scheda.
             </Typography>
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="outline"
              onClick={() => setShowSupportDialog(false)}
            >
              Annulla
            </Button>
            <Button
              onClick={confirmSupportRedirect}
              className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Headset className="h-4 w-4" />
              Apri Ticket
            </Button>
        </DialogActions>
      </Dialog>
    </header>
  )
}
